/**
 * Whether a factoring provider is linked for this project — the one question the box-truck drawer needs
 * before deciding whether a rating lookup is worth withholding.
 *
 * Three answers, and the third is the point. "Unknown" is NOT "unlinked": `fetchFactoringStatus`
 * swallows its errors and resolves null, and reading that as unlinked makes the gate fail OPEN. An
 * unlinked provider is rendered un-deferred on purpose — the chip stops at its own auth check, so there
 * is no lookup to save — but the chip re-runs the status call itself, so one transient error here plus a
 * success there fires a real RTS/TAFS portal lookup on drawer open, against the 30/min credential budget
 * and the circuit breaker, precisely while the backend is already struggling. Unknown defers instead:
 * that costs one press, and pressing it runs the chip's own status and rating path anyway.
 *
 * The answer is paired with the provider it describes so the caller can resolve it during RENDER. An
 * effect cannot: it runs after the commit in which the provider changed, and React flushes child effects
 * before parent ones, so the chip's fetch effect would already have started a lookup for a provider
 * nothing had confirmed.
 */

import { useEffect, useState } from 'react'
import { FactoringProvider, fetchFactoringStatus } from 'services/factoring'
import { getAuthenticatedProviders } from 'hooks/useFactoringData'

type ProviderLinkState = 'linked' | 'unlinked' | 'unknown'

/** How long `/factoring/status` may take before it is worth saying so out loud. Purely a report: see
 *  the timer below. */
const PROVIDER_STATUS_SLOW_MS = 5000

export const useProviderLinkState = (provider: FactoringProvider): ProviderLinkState => {
    const [answer, setAnswer] = useState<{ provider: FactoringProvider; state: ProviderLinkState } | null>(null)

    useEffect(() => {
        let cancelled = false
        const settle = (state: ProviderLinkState) => {
            if (!cancelled) setAnswer({ provider, state })
        }

        // A report, not a fallback. It used to settle 'unknown' to escape a blank skeleton the caller
        // rendered while pending; that skeleton is gone — pending already reads as 'unknown' and defers —
        // so settling here changed nothing, and a late answer overwrote it anyway. What remains is worth
        // keeping on its own: a stalled `/factoring/status` is otherwise invisible, since every caller
        // just goes on showing See Rating.
        const slowWarning = setTimeout(() => {
            if (cancelled) return
            console.warn(`[BoxTruckFactoring] /factoring/status has not answered in ${PROVIDER_STATUS_SLOW_MS}ms — ${provider} stays behind See Rating`)
        }, PROVIDER_STATUS_SLOW_MS)

        // `/factoring/status` is ours, not a provider's, and never touches RTS or TAFS. While the service
        // is healthy it is memoised for 30s at module scope, so opening loads in a row costs about two
        // calls a minute. That does NOT hold while it is failing: errors are not written to that cache
        // (`services/factoring/auth.ts:75-83`), so every caller re-asks.
        void fetchFactoringStatus()
            .then((status) => {
                clearTimeout(slowWarning)
                // A null answer is a swallowed failure, not evidence the provider is unlinked.
                if (status === null) return settle('unknown')
                settle(getAuthenticatedProviders(status).includes(provider) ? 'linked' : 'unlinked')
            })
            // Unreachable while fetchFactoringStatus swallows its own errors, but nothing enforces that:
            // an interceptor rethrowing outside its try would reject, and without this the caller would
            // hold behind See Rating forever with nothing in the console to explain it.
            .catch((error) => {
                clearTimeout(slowWarning)
                // Loudly, because this branch exists for a case that should not happen: reaching it means
                // the invariant that fetchFactoringStatus never rejects has broken. Swallowing it would
                // leave a stuck See Rating with nothing in the console to explain it.
                console.error('[BoxTruckFactoring] /factoring/status rejected — holding %s behind See Rating', provider, error)
                settle('unknown')
            })

        return () => {
            cancelled = true
            clearTimeout(slowWarning)
        }
    }, [provider])

    // Pending, and any answer belonging to a provider the caller has since moved off, both read as
    // unknown — which is what makes this safe to consume during render.
    return answer && answer.provider === provider ? answer.state : 'unknown'
}
